/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';

const partnerTypes = [
  { value: '1', label: 'Partner' },
  { value: '2', label: 'Institution' },
];

const defaultFormData = {
  name: '',
  partner_type: '',
  address_line_1: '',
  street: '',
  zip_code: '',
  gstin: '',
};

const fieldCls =
  'w-full h-9 px-3 py-2 rounded-lg border border-input text-sm text-foreground bg-background outline-none font-[inherit] transition-all placeholder:text-muted-foreground focus:border-primary focus:shadow-[0_0_0_3px_rgba(32,104,178,0.12)] disabled:opacity-50 disabled:cursor-not-allowed';

const labelCls =
  'block text-[12.5px] font-[560] text-foreground-strong tracking-[0.005em] mb-1.5';

const chevronSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23919BAA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`;

export const PartnerModal = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [gstinError, setGstinError] = useState('');

  const requiredFields = [
    'name',
    'partner_type',
    'address_line_1',
    'street',
    'zip_code',
    'gstin',
  ];

  const hasMissingRequiredFields = () =>
    requiredFields.some((field) => {
      const value = formData[field];
      return typeof value === 'string' ? !value.trim() : !value;
    });

  const isGSTINValid = formData.gstin?.trim().length === 15 && !gstinError;
  const isFormValid = !hasMissingRequiredFields() && isGSTINValid;

  const validateGSTIN = (value) => {
    const gstinValue = value?.trim() ?? '';
    if (!gstinValue) {
      setGstinError('GSTIN is required');
      return false;
    }
    if (gstinValue.length !== 15) {
      setGstinError('GSTIN must be exactly 15 characters');
      return false;
    }
    setGstinError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateGSTIN(formData.gstin)) return;
    if (!isFormValid) return;
    onSubmit({
      name: formData.name.trim(),
      partner_type: parseInt(formData.partner_type, 10),
      address_line_1: formData.address_line_1.trim(),
      street: formData.street.trim(),
      zip_code: formData.zip_code.trim(),
      gstin: formData.gstin.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
        {/* Organisation Details */}
        <div>
          <p className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground mb-3">
            Organisation Details
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className={labelCls}>
                Partner Name <span className="text-destructive ml-0.5">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g. Acme Bank"
                required
                disabled={loading}
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="partnerType" className={labelCls}>
                Partner Type <span className="text-destructive ml-0.5">*</span>
              </label>
              <select
                id="partnerType"
                value={formData.partner_type}
                onChange={(e) =>
                  setFormData({ ...formData, partner_type: e.target.value })
                }
                required
                disabled={loading}
                className={`${fieldCls} cursor-pointer appearance-none pr-8`}
                style={{
                  backgroundImage: chevronSvg,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                }}
              >
                <option value="" disabled>
                  Select type
                </option>
                {partnerTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address & Tax */}
        <div>
          <div className="h-px bg-border mb-4" />
          <p className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground mb-3">
            Address &amp; Tax
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="addressLine1" className={labelCls}>
                Address Line 1{' '}
                <span className="text-destructive ml-0.5">*</span>
              </label>
              <input
                id="addressLine1"
                type="text"
                value={formData.address_line_1}
                onChange={(e) =>
                  setFormData({ ...formData, address_line_1: e.target.value })
                }
                placeholder="Enter address line 1"
                required
                disabled={loading}
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="street" className={labelCls}>
                Street <span className="text-destructive ml-0.5">*</span>
              </label>
              <input
                id="street"
                type="text"
                value={formData.street}
                onChange={(e) =>
                  setFormData({ ...formData, street: e.target.value })
                }
                placeholder="Enter street"
                required
                disabled={loading}
                className={fieldCls}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="zipCode" className={labelCls}>
                Zip Code <span className="text-destructive ml-0.5">*</span>
              </label>
              <input
                id="zipCode"
                type="text"
                value={formData.zip_code}
                onChange={(e) =>
                  setFormData({ ...formData, zip_code: e.target.value })
                }
                placeholder="Enter zip code"
                required
                disabled={loading}
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="gstin" className={labelCls}>
                GSTIN <span className="text-destructive ml-0.5">*</span>
              </label>
              <input
                id="gstin"
                type="text"
                value={formData.gstin}
                onChange={(e) => {
                  const { value } = e.target;
                  setFormData({ ...formData, gstin: value });
                  validateGSTIN(value);
                }}
                placeholder="e.g. 29ABCDE1234F1Z6"
                required
                disabled={loading}
                maxLength={15}
                className={`${fieldCls} font-mono text-[12.5px] tracking-[0.02em] ${
                  gstinError
                    ? 'border-destructive focus:border-destructive focus:shadow-[0_0_0_3px_rgba(220,38,38,0.08)]'
                    : ''
                }`}
              />
              {gstinError && (
                <p className="text-[11.5px] text-destructive mt-1.5">
                  {gstinError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-[22px] py-[14px] flex justify-end gap-2 flex-shrink-0 bg-brand-bg-subtle border-t border-border">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading || !isFormValid}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Partner'
          )}
        </Button>
      </div>
    </form>
  );
};

export default PartnerModal;
